import React, { useState, useEffect } from 'react';
import { FaDownload, FaExpand, FaCompress, FaFilePdf, FaFileWord, FaEye } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import * as api from '../../../../api/apiConnector.js';

/**
 * UserGuideDisplay Component
 * 
 * Displays user guide file in the compiler interface
 * Shows PDF preview in iframe or provides download link for other formats
 */
const UserGuideDisplay = ({ 
  form, 
  currentUser, 
  colors, 
  isSimulation = false,
  isCollapsed = false,
  onToggleCollapse
}) => {
  const { t } = useTranslation();
  
  const [guideFile, setGuideFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Check if user guide should be shown (consistent with questions pattern)
  const shouldShowGuide = form?.showUserGuide && form?.userGuideFiles?.[0];

  /**
   * Load guide file data
   */
  useEffect(() => {
    const currentGuideFile = form?.userGuideFiles?.[0];
    if (shouldShowGuide && currentGuideFile) {
      setGuideFile(currentGuideFile);
      
      // If it's a PDF, load it for preview (skip in simulation mode to avoid API errors)
      if (currentGuideFile.MimeType === 'application/pdf' && !isSimulation) {
        loadPdfPreview(currentGuideFile);
      }
    }
  }, [form?.userGuideFiles, shouldShowGuide, isSimulation]);

  /**
   * Load PDF preview using DMS API (same pattern as questions)
   * In simulation mode, handles errors gracefully without showing them to user
   */
  const loadPdfPreview = async (file) => {
    try {
      setLoading(true);
      setError(null);
      
      // Get PDF blob from DMS API (same pattern as other files)
      const blob = await api.downloadFileGet(`dms/downloadFile/get/${file.Id}`, {
        user: currentUser()
      });
      
      // Create blob URL for PDF preview
      const blobUrl = window.URL.createObjectURL(blob);
      setPdfUrl(blobUrl);
      
    } catch (err) {
      console.error('Error loading PDF preview:', err);
      // In simulation mode, don't show error to user, just log it
      if (!isSimulation) {
        setError(t('failedToLoadGuide', { ns: 'labels', defaultValue: 'Impossibile caricare la guida utente.' }));
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle file download using DMS API (same pattern as questions)
   * In simulation mode, handles errors gracefully
   */
  const handleDownload = async () => {
    if (!guideFile) return;
    
    try {
      setLoading(true);
      
      // Download file blob from DMS API (same pattern as other files)
      const blob = await api.downloadFileGet(`dms/downloadFile/get/${guideFile.Id}`, {
        user: currentUser()
      });
      
      // Create download link and trigger download
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = guideFile.Name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the object URL
      window.URL.revokeObjectURL(downloadUrl);
      
    } catch (err) {
      console.error('Download error:', err);
      // In simulation mode, show user-friendly message instead of error
      if (isSimulation) {
        alert(t('simulationModeDownload', { ns: 'labels', defaultValue: 'Download non disponibile in modalità simulazione.' }));
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get appropriate icon for file type
   */
  const getFileIcon = () => {
    if (!guideFile?.Name) return <FaEye />;
    
    const extension = guideFile.Name.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return <FaFilePdf className="text-danger" />;
      case 'doc':
      case 'docx':
        return <FaFileWord className="text-primary" />;
      default:
        return <FaEye />;
    }
  };

  /**
   * Toggle fullscreen mode
   */
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  /**
   * Clean up blob URLs when component unmounts
   */
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  // Don't render if guide shouldn't be shown
  if (!shouldShowGuide || !guideFile) {
    return null;
  }

  // Fullscreen modal for PDF viewing
  if (isFullscreen && guideFile.MimeType === 'application/pdf' && pdfUrl) {
    return (
      <div 
        className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column"
        style={{ 
          backgroundColor: 'rgba(0, 0, 0, 0.95)', 
          zIndex: 1050 
        }}
      >
        <div 
          className="d-flex justify-content-between align-items-center p-3"
          style={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            borderBottom: '2px solid rgba(255, 255, 255, 0.2)' 
          }}
        >
          <h6 className="text-white mb-0">
            <i className="fa-solid fa-book-open me-2"></i>
            {guideFile.Name}
          </h6>
          <button
            type="button"
            className="btn btn-sm btn-light fw-bold"
            onClick={toggleFullscreen}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              color: '#000',
              border: '2px solid #fff',
              boxShadow: '0 2px 8px rgba(255, 255, 255, 0.3)',
              minWidth: '160px'
            }}
          >
            <FaCompress className="me-1" />
            {t('exitFullscreen', { ns: 'labels', defaultValue: 'Esci da schermo intero' })}
          </button>
        </div>
        
        <div className="flex-grow-1">
          <iframe
            src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=1`}
            className="w-100 h-100 border-0"
            title={guideFile.Name}
            style={{
              pointerEvents: 'auto',
              overflow: 'hidden'
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="card h-100">
      <div 
        className="card-header d-flex justify-content-between align-items-center"
        style={{ backgroundColor: colors?.secondaryLight || '#e9ecef' }}
      >
        <div className="d-flex align-items-center">
          {getFileIcon()}
          <span className="ms-2 fw-bold">
            {t('userGuide', { ns: 'labels', defaultValue: 'Guida Utente' })}
          </span>
          {!isCollapsed && guideFile.MimeType === 'application/pdf' && (
            <span className="ms-2 badge bg-success small">
              {t('livePreview', { ns: 'labels', defaultValue: 'Anteprima Live' })}
            </span>
          )}
        </div>
        
        <div className="d-flex gap-2">
          {guideFile.MimeType === 'application/pdf' && pdfUrl && (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={toggleFullscreen}
              title={t('fullscreen', { ns: 'labels', defaultValue: 'Schermo intero' })}
            >
              <FaExpand />
            </button>
          )}
          
          {/* Download button - commented out pending client decision
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={handleDownload}
            disabled={loading}
            title={t('download', { ns: 'labels', defaultValue: 'Scarica' })}
          >
            {loading ? (
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            ) : (
              <FaDownload />
            )}
          </button>
          */}
          
          {onToggleCollapse && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={onToggleCollapse}
              title={isCollapsed ? t('expand', { ns: 'labels', defaultValue: 'Espandi' }) : t('collapse', { ns: 'labels', defaultValue: 'Riduci' })}
            >
              <i className={`fa-solid ${isCollapsed ? 'fa-plus' : 'fa-minus'}`}></i>
            </button>
          )}
        </div>
      </div>
      
      {!isCollapsed && (
        <div className="card-body p-0" style={{ height: 'calc(100vh - 200px)', minHeight: '400px' }}>
          {loading && (
            <div className="d-flex justify-content-center align-items-center h-100">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}
          
          {error && (
            <div className="d-flex flex-column justify-content-center align-items-center h-100 p-4">
              <i className="fa-solid fa-exclamation-triangle fa-3x text-warning mb-3"></i>
              <h6 className="text-muted mb-3">{error}</h6>
              {/* Download button - commented out pending client decision
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleDownload}
              >
                <FaDownload className="me-1" />
                {t('downloadGuide', { ns: 'labels', defaultValue: 'Scarica Guida' })}
              </button>
              */}
            </div>
          )}
          
          {!loading && !error && (
            <>
              {guideFile.MimeType === 'application/pdf' && pdfUrl ? (
                // PDF preview
                <iframe
                  src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                  className="w-100 h-100 border-0"
                  title={guideFile.Name}
                  style={{
                    pointerEvents: 'auto',
                    overflow: 'hidden'
                  }}
                />
              ) : (
                // Non-PDF files - show download option
                <div className="d-flex flex-column justify-content-center align-items-center h-100 p-4">
                  <div className="mb-4">
                    {getFileIcon()}
                  </div>
                  
                  <h6 className="mb-3 text-center">{guideFile.Name}</h6>
                  
                  {/* Download message - commented out pending client decision
                  <p className="text-muted text-center mb-4">
                    {t('guideDocumentPreview', { 
                      ns: 'labels', 
                      defaultValue: 'Questo documento Word non può essere visualizzato direttamente. Scaricalo per visualizzare il contenuto della guida.' 
                    })}
                  </p>
                  */}
                  <p className="text-muted text-center mb-4">
                    {t('guideDocumentAvailable', { 
                      ns: 'labels', 
                      defaultValue: 'Guida utente disponibile per questo form.' 
                    })}
                  </p>
                  
                  {/* Download button - commented out pending client decision
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleDownload}
                    disabled={loading}
                  >
                    <FaDownload className="me-2" />
                    {t('downloadGuide', { ns: 'labels', defaultValue: 'Scarica Guida' })}
                  </button>
                  */}
                </div>
              )}
            </>
          )}
        </div>
      )}
      
      {/* Collapsed state */}
      {isCollapsed && (
        <div className="card-body text-center py-4">
          <div className="mb-2">
            {getFileIcon()}
          </div>
          <small className="text-muted d-block mb-2">
            {guideFile.Name}
          </small>
          {/* Download button - commented out pending client decision
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={handleDownload}
            disabled={loading}
          >
            <FaDownload className="me-1" />
            {t('download', { ns: 'labels', defaultValue: 'Scarica' })}
          </button>
          */}
        </div>
      )}
    </div>
  );
};

export default UserGuideDisplay;
